<template>
  <v-dialog v-model="dialog" :retain-focus="false" persistent :fullscreen="isMobile ? true : false" max-width="1100px">
    <v-card :loading="isLoadingSaveProlong">
      <v-card-title :class="isMobile ? 'pt-10' : 'headline'">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <span class="headline">{{ $t("bookings.prolong_short") }}</span>
            <v-spacer></v-spacer>
            <!-- Условие 1-в-1 как у обработчика BookingCard#saveProlong: иначе
                 кнопка была бы видна тем, у кого сохранение всё равно отобьётся.
                 Кнопка документа ниже — только сотруднику: продлить агент с
                 правом может, а договор по продлению печатает сотрудник. -->
            <v-btn icon class="ml-2 green--text" v-if="isStaff || agentCan('can_edit_booking')"
              @click="save()" :loading="isLoadingSaveProlong">
              <v-icon>mdi-content-save-all-outline</v-icon>
            </v-btn>
            <v-btn icon v-if="isStaff" color="warning"
              @click="generateProlongDoc(booking)" :loading="isLoadingGenerateProlongDoc" v-tooltip="{
                content: $t('bookings.generate_docs'),
                placement: 'bottom-center',
                classes: ['info'],
                targetClasses: ['it-has-a-tooltip'],
                delay: {
                  show: 500,
                  hide: 500,
                },
              }">
              <v-icon>mdi-file-document-outline</v-icon>
            </v-btn>
            <v-icon :class="$vuetify.theme.dark ? 'ml-2 white--text' : 'ml-2 black--text'" @click="close()">
              mdi-close
            </v-icon>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-simple-table>
              <tbody>
                <tr>
                  <th>{{ $t("bookings.booking") }} {{ $t("custom.number") }}</th>
                  <td>{{ booking.id }}</td>
                </tr>
                <tr>
                  <th>{{ $t("bookings.client") }}</th>
                  <td>
                    <router-link :to="{ path: `/clients/${booking.client_id}` }">
                      {{ booking.last_name }} {{ booking.first_name }} {{ booking.middle_name }}
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <th>{{ $t("bookings.booking_end") }}</th>
                  <td>{{ booking.end_date }}</td>
                </tr>
                <tr :class="$vuetify.theme.dark ? 'text-overline grey darken-1' : 'text-overline grey lighten-1'">
                  <th :class="isMobile ? 'text-center' : ''" @click="copyToBuffer(booking.selected_price)"
                    style="cursor: pointer;">{{ $t("bookings.current_price") }}</th>
                  <td :class="isMobile ? 'text-center' : ''" @click="copyToBuffer(booking.selected_price)"
                    style="cursor: pointer;">{{ booking.selected_price }}{{ findCurrencySymbol(base_currency_id) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
          <v-col v-if="!booking.hourly && !booking.weekly && !booking.monthly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.days" :label="$t('prolongs.for_days')" type="number" :min="1"
                  :max="prolongUnitsLimit()" :error-messages="prolongUnitsErrorMessages()"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  style="cursor: pointer;" :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.hourly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.hourly_hours" :label="$t('prolongs.for_hours')" type="number"
                  :min="1" :max="prolongUnitsLimit()" :error-messages="prolongUnitsErrorMessages()"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.weekly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.weekly_weeks" :label="$t('prolongs.for_weeks')" type="number"
                  :min="1" :max="prolongUnitsLimit()" :error-messages="prolongUnitsErrorMessages()"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.monthly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.monthly_months" :label="$t('prolongs.for_months')"
                  type="number" :min="1" :max="prolongUnitsLimit()" :error-messages="prolongUnitsErrorMessages()"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <!-- FT-084 STEP-12 / доработка 2026-07-15 (решение USER): продление
               живёт по СВОБОДНОЙ цене оператора (клиенту часто дают скидку),
               серверный расчёт — только справка. Показываем расчётную цену
               ЗА ЕДИНИЦУ (ставка/сутки по ступени новой длительности; итог к
               оплате и так виден ниже: дни × цена); «Использовать» подставляет
               её в поле цены — по желанию. -->
          <v-col cols="12" v-if="serverPreviewStatus !== 'idle'">
            <!-- :icon="false" — иконка типа занимала левый край, из-за неё строка
                 стояла не по центру баннера (замечание USER 2026-08-19). Цвет
                 от type сохраняется. -->
            <v-alert :value="true" :type="serverPreviewStatus === 'error' ? 'error' : 'info'" dense outlined
              :icon="false" class="mb-0" data-cy="prolong_server_preview">
              <div v-if="serverPreviewStatus === 'loading'" class="text-overline text-center">
                {{ $t("bookings.quote_loading") }}
              </div>
              <div v-else-if="serverPreviewStatus === 'error'" class="text-overline text-center">
                {{ $t("bookings.quote_error") }}
              </div>
              <!-- Раскрывающаяся панель: в заголовке — цена за единицу и кнопка
                   подстановки, внутри — из чего эта цена сложилась. Полная
                   разбивка нужна не каждый раз, поэтому по умолчанию свёрнута
                   (решение USER 2026-08-19). Без разбивки (битый ответ) панель
                   заперта — раскрывать нечего. -->
              <v-expansion-panels v-else-if="serverPreviewStatus === 'ready'" v-model="breakdownPanel" flat
                class="prolong-preview-panels">
                <v-expansion-panel :disabled="!prolongBreakdown">
                  <v-expansion-panel-header data-cy="prolong_explain_toggle">
                    <div class="d-flex align-center justify-center flex-wrap text-overline">
                      <span>{{ $t(serverPreviewRateLabelKey) }}:
                        <span data-cy="prolong_server_preview_cost">{{ serverPreviewUnitPrice }}</span>{{ findCurrencySymbol(base_currency_id) }}
                      </span>
                      <!-- .stop: подстановка цены не должна заодно раскрывать панель. -->
                      <v-btn class="ml-2" x-small outlined color="secondary" data-cy="prolong_use_server_price"
                        @click.stop="applyServerPreviewPrice()">
                        {{ $t("prolongs.use_server_price") }}
                      </v-btn>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- Строки — те же, что панель «Сезоны» в карточке брони, плюс
                         слои после сезонов (тариф, скидка) и итог продления по
                         этой ставке: в заголовке стоит ЦЕНА ЗА ЕДИНИЦУ, и именно
                         это чтение сбивало операторов. -->
                    <!-- text--primary: содержимое панели — обычный текст темы, а не
                         голубой цвет баннера. Голубым остаётся заголовок (он и есть
                         баннер), внутри же это мешает читать. Класс Vuetify, а не свой
                         цвет: он сам разный в светлой и тёмной теме. -->
                    <div v-if="prolongBreakdown" class="prolong-breakdown text-left text--primary"
                      data-cy="prolong_breakdown">
                      <div>
                        {{ $t("prolongs.explain_unit_price_is_rate", { price_label: $t(prolongBreakdown.unitKeys.price) }) }}
                      </div>
                      <div v-if="prolongBreakdown.unitsBefore && prolongBreakdown.unitsAfter">
                        {{ $t("prolongs.explain_duration", {
                          before: prolongBreakdown.unitsBefore,
                          after: prolongBreakdown.unitsAfter,
                          unit: $t(prolongBreakdown.unitKeys.count),
                        }) }}
                      </div>
                      <v-simple-table v-if="prolongBreakdown.rows.length > 0" dense class="my-1">
                        <template v-slot:default>
                          <tbody>
                            <tr v-for="row in prolongBreakdown.rows" :key="row.index">
                              <th class="text-left font-weight-regular">
                                <template v-if="row.isProlong">
                                  {{ $t("bookings.prolong_segment_row") }}
                                </template>
                                <template v-else>
                                  {{ row.startDate }} - {{ row.endDate }}
                                  <span v-if="row.isBase" class="grey--text">
                                    ({{ $t("bookings.base_prices_row") }})
                                  </span>
                                </template>
                              </th>
                              <td class="text-left">{{ row.qty }}</td>
                              <td class="text-right">{{ withCurrency(row.unitPrice) }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                      <div v-if="prolongBreakdown.mixedRates">{{ $t("prolongs.explain_average_rate") }}</div>
                      <div v-if="prolongBreakdown.priceNoSale !== null">
                        {{ $t("prolongs.explain_no_sale", { price: withCurrency(prolongBreakdown.priceNoSale) }) }}
                      </div>
                      <div v-if="prolongBreakdown.tariffAdjustment !== null">
                        {{ $t("prolongs.explain_tariff", { sum: withCurrency(prolongBreakdown.tariffAdjustment) }) }}
                      </div>
                      <div v-if="prolongBreakdown.tariffMissing" class="red--text"
                        data-cy="prolong_breakdown_tariff_missing">
                        {{ $t("prolongs.explain_tariff_not_applied") }}
                      </div>
                      <div v-if="prolongBreakdown.discount !== null">
                        {{ $t("prolongs.explain_discount", { sum: withCurrency(prolongBreakdown.discount) }) }}
                      </div>
                      <div v-if="seasonModeText" data-cy="prolong_breakdown_season_mode">
                        {{ $t("bookings.season_mode_label") }}: {{ seasonModeText }}
                      </div>
                      <div v-if="priceSourceText">{{ priceSourceText }}</div>
                      <div v-if="extraDayReasonText" class="red--text">{{ extraDayReasonText }}</div>
                      <div v-if="prolongBreakdown.addedCost !== null" class="font-weight-bold mt-1"
                        data-cy="prolong_breakdown_result">
                        {{ $t("prolongs.explain_result", {
                          units: prolongBreakdown.unitsAdded,
                          price: withCurrency(prolongBreakdown.unitPrice),
                          total: withCurrency(prolongBreakdown.addedCost),
                        }) }}
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-alert>
          </v-col>
          <v-col v-if="!booking.hourly && !booking.weekly && !booking.monthly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.days && prolongData.days != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.days * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.days * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.days *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.hourly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.hourly_hours && prolongData.hourly_hours != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.hourly_hours * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.hourly_hours * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.hourly_hours *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.weekly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.weekly_weeks && prolongData.weekly_weeks != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.weekly_weeks * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.weekly_weeks * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.weekly_weeks *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.monthly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.monthly_months && prolongData.monthly_months != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.monthly_months * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.monthly_months * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.monthly_months *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <!-- Аренда -->
          <v-col cols="12">
            <v-row align="center">
              <v-col auto>
                <v-text-field v-model.number="prolongData.sum" :label="$t('prolongs.get_for_rent')"
                  :messages="messageInBaseCurrency(prolongData.sum, prolongData.currency_id)" dense
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col auto v-if="company && company.multicurrency_cashboxes">
                <v-select v-model="prolongData.company_cashbox" :items="active_company_cashboxes"
                  :item-text="item => item.name" :return-object="true"
                  @change="prolongData.currency_id = prolongData.company_cashbox.currency_id, prolongData.company_cashbox_id = prolongData.company_cashbox.id"
                  :label="$t('companies.currency')" dense :disabled="isLoadingSaveProlong"></v-select>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-row align="center">
              <v-col cols="12" class="d-flex align-center justify-space-between">
                <v-checkbox v-if="company && company.cash_payment" v-model="prolongData.type" color="success"
                  value="Наличные" :disabled="prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.cashless_payment" v-model="prolongData.type" color="warning"
                  value="Терминал" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Наличные' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.card_to_card_payment" v-model="prolongData.type" color="info"
                  value="На карту" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.entity_payment" v-model="prolongData.type" color="secondary"
                  class="mt-0 mr-0" dense hide-details value="Р/с" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Баланс клиента'
                    " :style="prolongData.type == 'Наличные' ||
                      prolongData.type == 'Терминал' ||
                      prolongData.type == 'На карту' ||
                      prolongData.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                        content: $t('tooltips.entity_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.client_payment" v-model="prolongData.type" color="error"
                  class="mt-0 mr-0" dense hide-details value="Баланс клиента" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с'
                    " :style="prolongData.type == 'Наличные' ||
                      prolongData.type == 'Терминал' ||
                      prolongData.type == 'На карту' ||
                      prolongData.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                        content: $t('money.client_balance'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close()">{{ $t("custom.close") }}</v-btn>
        <!-- Решение USER 2026-07-15: одна кнопка сохранения. Продление всегда
             идёт легаси-путём save_prolong со СВОБОДНОЙ ценой оператора
             (+опц. оплата); при пустой сумме оплаты save() показывает
             confirm «продлить без оплаты?» (prolong_zero_title). Отдельная
             серверная кнопка «Продлить без оплаты» (STEP-12) удалена — она
             игнорировала вбитую цену и дублировала легаси-возможность. -->
        <v-btn color="success" @click="save()" :loading="isLoadingSaveProlong" data-cy="prolong_save_legacy">{{ $t("custom.save") }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { copyToClipboard as copyTextToClipboard } from '@/utils/clipboard';
import PizZip from "pizzip";
import { saveAs } from "file-saver";
import Docxtemplater from "docxtemplater";
import { buildProlongBreakdown, unitLabelKeys } from "./prolongBreakdown";
import { extraDayReasonKey } from "./extraDayReason";
import { MAX_PROLONG_UNITS, prolongUnitsOverLimit } from "./bookingQuote";
export default {
  name: 'ProlongDialog',
  data() {
    return {
      prolongData: {
        days: 0,
        hourly_hours: 0,
        monthly_months: 0,
        weekly_weeks: 0,
        price: 0,
        additional_hours: 0,
        cash: 0,
        cashless: 0,
        cash_card: 0,
        entity: 0,
        client: 0,
        currency_id: null,
        company_cashbox_id: null,
        company_cashbox: null,
      },
      paymentsPanel: [0],
      isLoadingGenerateProlongDoc: false,
      error: null,
      // FT-084 STEP-12 (CHK-06): server-side prolong preview state.
      // 'idle' (no units entered yet) | 'loading' | 'ready' | 'error'.
      serverPreviewStatus: "idle",
      serverPreviewUnitPrice: null, // string per-unit price — показывается в превью
      // Полный ответ квоты — источник разбивки «из чего сложилась цена»
      // (2026-08-19). Прежде из него брали только selected_price.
      serverPreviewQuote: null,
      // v-model одиночной expansion-панели: null = свёрнута, 0 = раскрыта.
      breakdownPanel: null,
      // Тариф, догруженный по booking.tariff_id, когда справочника карточки не
      // хватило (бронь чужого филиала). id держим отдельно, чтобы не принять
      // тариф прошлой брони за текущий.
      fetchedTariff: null,
      fetchedTariffId: null,
      previewTimer: null,
      previewSeq: 0,
    }
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    set_car: {
      type: Object,
      required: true
    },
    add_drivers: {
      type: Array,
      default: () => []
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: ''
    },
    // user_role раньше приходил пропом от BookingCard, который брал его из
    // того же стора. Теперь его отдаёт глобальный roleMixin — проп убран,
    // иначе Vue 2 ругается «computed property is already defined as a prop».
    company: {
      type: Object,
      default: null
    },
    company_cashboxes: {
      type: Array,
      default: () => []
    },
    worker_accounts: {
      type: Array,
      default: () => []
    },
    showSelectWorkerCashbox: {
      type: Boolean,
      default: false
    },
    company_cashbox_id: {
      type: [Number, String],
      default: null
    },
    isLoadingSaveProlong: {
      type: Boolean,
      default: false
    },
    // Тариф брони (объект справочника: {id, sum, percent}) — карточка уже
    // держит справочник, второй запрос ради превью не нужен.
    booking_tariff: {
      type: Object,
      default: null
    },
  },
  mounted() {
    if (this.base_currency_id && this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
      this.prolongData.currency_id = this.base_currency_id;
      this.prolongData.company_cashbox = this.active_company_cashboxes.find((cashbox) => cashbox.is_default);
      this.prolongData.company_cashbox_id = this.prolongData.company_cashbox.id;
    }
    // Диалог persistent: если он смонтирован уже открытым, watch value не
    // сработает — тариф надо запросить здесь.
    if (this.value) this.ensureBookingTariff();
  },
  beforeDestroy() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewSeq += 1; // invalidate any in-flight preview response
  },
  watch: {
    // FT-084 STEP-12 (CHK-06): debounced server preview whenever the
    // manager types a unit count. Purely additive UX — does not touch
    // prolongData.price unless the manager explicitly clicks
    // "use server price" (applyServerPreviewPrice).
    "prolongData.days"() { this.requestServerPreview(); },
    "prolongData.hourly_hours"() { this.requestServerPreview(); },
    "prolongData.weekly_weeks"() { this.requestServerPreview(); },
    "prolongData.monthly_months"() { this.requestServerPreview(); },
    // V1 (dual-line review): диалог persistent — при закрытии компонент НЕ
    // уничтожается (beforeDestroy не срабатывает), поэтому поздний ответ
    // превью мог выставить "ready" в скрытом диалоге и показаться при
    // следующем открытии. Сбрасываем/инвалидируем превью на закрытии.
    value(open) {
      if (!open) {
        this.resetServerPreview();
        return;
      }
      this.ensureBookingTariff();
    },
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    active_company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id && cashbox.is_active) {
            cashboxes.push(cashbox);
          }
        });
        cashboxes.sort((a, b) => {
          // сортировка по is_default
          if (a.is_default && !b.is_default) {
            return -1;
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    base_currency_id() {
      if (this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
        return this.active_company_cashboxes.find((cashbox) => cashbox.is_default).currency_id;
      } else {
        return null;
      }
    },
    exchange_rates() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.exchange_rates) {
        return this.$store.getters.getCompany.company.exchange_rates;
      } else {
        return [];
      }
    },
    // ------------------------------------------------------------------
    // «Из чего складывается расчётная цена продления» (2026-08-19).
    // Данные уже приходят в том же ответе квоты — новых запросов нет.
    // ------------------------------------------------------------------
    // Тариф брони для квоты: карточка держит справочник тарифов (грузит вместе
    // с бронью) и отдаёт найденный объект пропом. Нет тарифа у брони или
    // справочник ещё не доехал — null, движок считает без тарифа.
    bookingTariffPayload() {
      const tariff = this.booking_tariff || this.matchingFetchedTariff;
      if (!tariff) return null;
      return { sum: tariff.sum, percent: tariff.percent, id: tariff.id };
    },
    // Догруженный тариф годится, только если он от ТЕКУЩЕЙ брони: диалог
    // persistent и переживает переход между бронями.
    matchingFetchedTariff() {
      if (!this.fetchedTariff || !this.booking.tariff_id) return null;
      return this.fetchedTariffId == this.booking.tariff_id ? this.fetchedTariff : null;
    },
    prolongBreakdown() {
      if (this.serverPreviewStatus !== "ready") return null;
      return buildProlongBreakdown(this.serverPreviewQuote, {
        // Тариф у брони есть, а объекта нет — справочник карточки не покрывает
        // чужой филиал (bookings скоупятся по main_company, tariffs — по company).
        tariffUnavailable: !!this.booking.tariff_id && !this.bookingTariffPayload,
        unitsAdded: this.currentProlongUnits(),
        unitsBefore: this.currentBookingUnits(),
        rentalType: this.currentRentalType(),
      });
    },
    // Подпись баннера называет то, что в нём стоит: цену ЗА ЕДИНИЦУ тарификации
    // брони. Прежнее «Расчетная стоимость продления» обещало сумму — с этого
    // чтения и начиналось непонимание (отзыв сотрудника 2026-08-19).
    serverPreviewRateLabelKey() {
      return `prolongs.server_preview_rate_${this.currentRentalType()}`;
    },
    // Режим сезонов компании МАШИНЫ — как его сообщил движок (meta.season_mode).
    // Список закрыт: неизвестный режим из будущей версии не должен печатать
    // сырой i18n-ключ (vue-i18n возвращает сам ключ, когда перевода нет).
    seasonModeText() {
      const key = this.prolongBreakdown && this.prolongBreakdown.seasonMode;
      if (!["both", "start", "end", "off"].includes(key)) return "";
      return this.$t("bookings.season_mode_" + key);
    },
    priceSourceText() {
      const source = this.prolongBreakdown && this.prolongBreakdown.priceSource;
      if (!source) return "";
      return this.$t(source === "stored"
        ? "prolongs.explain_price_source_stored"
        : "prolongs.explain_price_source_current");
    },
    // Почему движок добавил сутки за остаток часов — та же подсказка, что в
    // карточке брони; здесь особенно к месту: добавленные сутки меняют
    // ступень длительности, а значит и саму ставку.
    extraDayReasonText() {
      const key = extraDayReasonKey(this.serverPreviewQuote);
      return key ? this.$t(key) : "";
    },
  },
  methods: {
    // Подписант со стороны клиента (docx) — зеркалит серверную логику client_signer:
    // юрлицо → директор/название фирмы; физлицо → fio с фолбэком на части имени.
    docxSignerName(client) {
      if (!client) return "      ";
      if (client.entity) return client.ceo || client.entity_name || "      ";
      const parts = `${client.lastname || ""} ${client.name || ""} ${client.middlename || ""}`
        .replace(/\s+/g, " ")
        .trim();
      return client.fio || parts || "      ";
    },
    // Потолок юнитов одного продления — зеркало серверного ProlongUnitsLimit
    // (422 prolongs_units_too_large). Здесь он ловит опечатку «цена в поле
    // количества» до запроса: отказ после сохранения оператору ничего не
    // объясняет, а сегмент на тысячи суток запирает бронь навсегда.
    prolongUnitsLimit() {
      return MAX_PROLONG_UNITS[this.currentRentalType()] || null;
    },
    prolongUnitsOverLimit() {
      return prolongUnitsOverLimit(this.currentRentalType(), this.currentProlongUnits());
    },
    prolongUnitsErrorMessages() {
      if (!this.prolongUnitsOverLimit()) return [];
      return [this.prolongUnitsLimitText()];
    },
    // Единицы берём тем же ключом, что и разбивка продления (unitLabelKeys), —
    // иначе в одном диалоге «суток аренды» и «дней» соседствовали бы.
    prolongUnitsLimitText() {
      return this.$t("prolongs.units_over_limit", {
        limit: this.prolongUnitsLimit(),
        units: this.$t(unitLabelKeys(this.currentRentalType()).count),
      });
    },
    save() {
      if (this.prolongUnitsOverLimit()) {
        this.$notify(this.$t("prolongs.units_over_limit_title"), this.prolongUnitsLimitText(), "error", 6000);
        return;
      }
      if (this.prolongData.currency_id != this.base_currency_id && this.prolongData.type == "Баланс клиента") {
        this.$notify(this.$t("errors.client_payments_in_non_base_currency"), "", "error", 4000);
        return;
      }
      // проверяем на должника
      if (this.$store.getters.getMainCompany && this.$store.getters.getMainCompany.payer_state == 3) {
        this.$notify(this.$t("errors.payer_state_3_title"), this.$t("errors.payer_state_3_text"), "warning", 10000);
        return;
      }
      this.$emit('save', this.prolongData);
    },
    close() {
      this.resetServerPreview();
      this.$emit('close');
    },
    // V1: единая точка сброса справочного превью (закрытие / value=false).
    resetServerPreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewSeq += 1; // инвалидирует любой in-flight ответ
      this.serverPreviewStatus = "idle";
      this.serverPreviewUnitPrice = null;
      this.serverPreviewQuote = null;
      this.breakdownPanel = null;
    },
    // ------------------------------------------------------------------
    // FT-084 STEP-12 / доработка 2026-07-15: справочное превью продления.
    // Additive to save()/legacy save_prolong above — this path never touches
    // payments/cashboxes, it only extends booking.end_date and adds the
    // price delta (POST /api/v1/priced_bookings/:id/prolong).
    // ------------------------------------------------------------------
    currentRentalType() {
      if (this.booking.hourly) return "hourly";
      if (this.booking.weekly) return "weekly";
      if (this.booking.monthly) return "monthly";
      return "daily";
    },
    // The unit count the manager typed for the current rental type (0 if none yet).
    currentProlongUnits() {
      if (this.booking.hourly) return this.prolongData.hourly_hours || 0;
      if (this.booking.weekly) return this.prolongData.weekly_weeks || 0;
      if (this.booking.monthly) return this.prolongData.monthly_months || 0;
      return this.prolongData.days || 0;
    },
    // Текущая длительность брони в её собственных единицах — то же число, что
    // печатает карточка. Служит «было» в паре «было → станет» (см.
    // prolongBreakdown: вычислять его вычитанием нельзя).
    currentBookingUnits() {
      if (this.booking.hourly) return Number(this.booking.hourly_hours) || 0;
      if (this.booking.weekly) return Number(this.booking.weekly_weeks) || 0;
      if (this.booking.monthly) return Number(this.booking.monthly_months) || 0;
      return Number(this.booking.days) || 0;
    },
    // New end_at (CTR-01 "DD-MM-YYYY HH:mm", zero-padded hour — the server's
    // DATE_FORMAT_REGEX /\A\d{2}-\d{2}-\d{4} \d{2}:\d{2}\z/ requires exactly
    // 2 digits; moment's `H` token is NOT zero-padded for single-digit
    // hours, so this deliberately uses `HH` unlike some legacy `H:mm`
    // call sites elsewhere in this codebase that only ever fed the
    // non-strict legacy save_prolong endpoint) = booking.end_date + typed
    // units, in the booking's own rental-type unit — same arithmetic
    // saveProlong() already uses for the legacy payload's `end_date`.
    prolongEndAt(units) {
      const unitName = this.booking.hourly ? "hours" : this.booking.weekly ? "weeks" : this.booking.monthly ? "months" : "days";
      return moment(this.booking.end_date, "DD-MM-YYYY H:mm").add(units, unitName).format("DD-MM-YYYY HH:mm");
    },
    // Справочное превью через ОДИН read-only POST /api/v1/price_quotes на
    // ПРОДЛЁННЫЙ интервал (start → end+units): подсказка = selected_price
    // этой квоты = ставка за единицу по ступени новой длительности (решение
    // USER 2026-07-15). Не дёргаем priced_bookings#prolong (у него нет
    // dry-run — он всегда мутирует бронь). Сохранение — легаси save() выше.
    requestServerPreview() {
      const units = this.currentProlongUnits();
      if (!units || units <= 0 || !this.booking.id) {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        this.previewSeq += 1;
        this.serverPreviewStatus = "idle";
        this.serverPreviewQuote = null;
        this.breakdownPanel = null;
        return;
      }

      if (this.previewTimer) clearTimeout(this.previewTimer);
      const mySeq = ++this.previewSeq;
      this.serverPreviewStatus = "loading";

      this.previewTimer = setTimeout(() => {
        const headers = { Authorization: this.$store.getters.getAuthToken };
        const priceSource = this.booking.apply_current_prices ? "current" : "stored";
        // Подсказка = ПРИМЕНИМАЯ ставка за сутки при НОВОЙ длительности брони
        // (решение USER 2026-07-15: база уже оплачена/зафиксирована, продление
        // идёт по новой цене). Раньше был quote-diff (новый итог − старый),
        // дававший марж­инальную дельту при переходе тарифной ступени
        // (напр. 8×1800 − 7×1900 = 1100) — противоречил сегментной модели
        // (база фиксирована) и путал оператора. Теперь берём selected_price
        // расширенной квоты — цену за сутки по ступени новой длительности.
        this.axios.post("/api/v1/price_quotes", {
          car_id: this.booking.car_id,
          booking_id: this.booking.id,
          rental_type: this.currentRentalType(),
          price_source: priceSource,
          sale: this.booking.sale || 0,
          sale_cash: this.booking.sale_cash || 0,
          rental_cost_sale: this.booking.rental_cost_sale || 0,
          rental_cost_sale_cash: this.booking.rental_cost_sale_cash || 0,
          price_hour: this.booking.price_hour,
          // Тариф брони участвует в расчёте (решение USER 2026-08-19: раньше
          // превью его не слало, и подсказка расходилась с ценой, которую
          // оператор видит в карточке). Форма {sum,percent,id} — как
          // BookingCard кладёт в свою квоту.
          tariff: this.bookingTariffPayload,
          start_at: this.booking.start_date,
          end_at: this.prolongEndAt(units),
        }, { headers })
          .then((response) => {
            if (mySeq !== this.previewSeq) return; // stale — молча игнорируем
            const unitPrice = Number(response.data.totals.selected_price);
            // NaN-guard (dual-line review): битый/отсутствующий selected_price
            // не должен показывать «NaN» и класть NaN в поле цены.
            if (!Number.isFinite(unitPrice)) {
              this.serverPreviewStatus = "error";
              this.serverPreviewQuote = null;
              return;
            }
            this.serverPreviewStatus = "ready";
            this.serverPreviewUnitPrice = unitPrice.toFixed(2);
            // Тот же ответ несёт разбивку (line_items/units/meta) — сохраняем
            // целиком, чтобы объяснение не стоило второго запроса.
            this.serverPreviewQuote = response.data;
          })
          .catch((error) => {
            if (mySeq !== this.previewSeq) return;
            this.serverPreviewStatus = "error";
            // Разбивка прошлой (удачной) квоты к новым входам не относится —
            // иначе баннер ошибки раскрывался бы в устаревший расчёт.
            this.serverPreviewQuote = null;
          });
      }, 350);
    },
    // Тариф брони для квоты. Карточка отдаёт объект пропом, но её справочник —
    // тарифы СВОЕГО филиала (`@company.tariffs`), а брони скоупятся по
    // main_company: у межфилиальной брони объекта нет. Тогда догружаем тариф
    // по id — это ровно тот тариф, который и так показан в карточке брони.
    // Не удалось (сеть, 404 у удалённого тарифа) — молчим и считаем без него,
    // об этом честно скажет строка разбивки (`tariffMissing`).
    ensureBookingTariff() {
      const tariffId = this.booking && this.booking.tariff_id;
      if (!tariffId || this.booking_tariff) return;
      if (this.fetchedTariffId == tariffId) return; // уже догружен

      this.axios
        .get(`api/v1/tariffs/${tariffId}`, { headers: { Authorization: this.$store.getters.getAuthToken } })
        .then((response) => {
          const tariff = response && response.data;
          if (!tariff || tariff.id == null) return;
          this.fetchedTariff = tariff;
          this.fetchedTariffId = tariff.id;
          // Превью, посчитанное до догрузки, тариф не содержало — пересчитываем.
          if (this.serverPreviewStatus !== "idle") this.requestServerPreview();
        })
        .catch(() => {
          // Тихо: подсказка справочная, ронять диалог из-за неё нельзя.
        });
    },
    // Символ валюты — внутрь значения плейсхолдера, а не в конец фразы:
    // в части языков плейсхолдер стоит в середине предложения («Der Tarif hat
    // die Mietkosten um {sum} verändert»), и приклеенный к концу знак валюты
    // оказался бы после последнего слова.
    withCurrency(value) {
      const symbol = this.findCurrencySymbol(this.base_currency_id);
      // Копейки — как в самом баннере (serverPreviewUnitPrice = toFixed(2)):
      // «1800€» в разбивке против «1800.00€» в баннере читались бы как разные
      // числа, а разбивка существует именно чтобы объяснить баннер.
      const num = Number(value);
      return `${Number.isFinite(num) ? num.toFixed(2) : value}${symbol}`;
    },
    applyServerPreviewPrice() {
      if (this.serverPreviewUnitPrice != null) {
        this.prolongData.price = Number(this.serverPreviewUnitPrice);
      }
    },
    sumInBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) / parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    sumInNonBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) * parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    messageInBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(this.base_currency_id)}`;
      } else {
        return '';
      }
    },
    messageInNonBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInNonBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(currency_id)}`;
      } else {
        return '';
      }
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    generateProlongDoc(booking) {
      if (this.isStaff) {
        this.isLoadingGenerateProlongDoc = true;
        let self = this;
        let prolong_data = this.prolongData;
        this.axios
          .get(`api/v1/prolong_templates`, {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          })
          .then((response) => {
            let content, error, url;
            if (response.data && response.data.url) {
              url = response.data.url;
            } else {
              if (self.$i18n.locale == "ru") {
                url = "https://web.rentprog.ru/prolong_example.docx";
              } else {
                url = "https://web.rentprog.com/prolong_example_en.docx";
              }
            }
            this.axios
              .get(url, {
                responseType: "arraybuffer",
                timeout: 120000, // выгрузка шаблона может не влезть в глобальные 30с на медленной сети
                headers: {
                  "Content-Type": "text/plain;charset=utf-8",
                },
              })
              .then((response) => {
                content = response.data;
                var zip = new PizZip(content);
                let client = self.client;
                let car = self.set_car;
                let add_drivers_info = "";
                if (self.add_drivers && self.add_drivers.length > 0) {
                  self.add_drivers.forEach((driver) => {
                    add_drivers_info += `${driver.lastname} ${driver.name} ${driver.middlename
                      }, ${self.$t("custom.passport")}: ${driver.passport_series
                      } ${driver.passport_number}, ${self.$t(
                        "custom.driver_license"
                      )}: ${driver.driver_series} ${driver.driver_number}${self.add_drivers.length > 1 ? ", " : ""
                      }`;
                  });
                }
                // Считаю предоплату
                let prepaid = 0;
                if (booking.counts && booking.counts.length > 0) {
                  booking.counts.forEach((count) => {
                    if (count.completed) {
                      if (this.company.multicurrency_cashboxes && this.company.multicurrency_cashboxes == true && count.rated_sum != null) {
                        let ratedSum = Number(count.rated_sum);
                        prepaid += ratedSum;
                      } else {
                        let sum = Number(count.sum);
                        prepaid += sum;
                      }
                    }
                  });
                  // Округляем сумму до 2 знаков после запятой
                  prepaid = parseFloat(prepaid.toFixed(2));
                }
                // Считаю доп сервисы
                let additional_services =
                  (booking.hours_cost > 0
                    ? booking.additional_hours * booking.price_hour
                    : 0) +
                  booking.hours_cost_end +
                  booking.delivery +
                  booking.delivery_end +
                  booking.equipment +
                  (booking.addons_cost || 0) + // FT-102 REQ-07: каталожное оборудование
                  booking.clean_payment +
                  booking.damage +
                  booking.gas +
                  booking.add_drivers_cost +
                  booking.insurance +
                  booking.fine +
                  booking.other +
                  booking.other_end;

                // Считаю комиссию в сутки
                let commission = booking.aggr_commission;
                let commission_day = commission / (booking.days > 0 ? booking.days : 1);

                var doc = new Docxtemplater().loadZip(zip);
                doc.setOptions({ nullGetter: () => "" });
                doc.setData({
                  prolong_sum: prolong_data.price * prolong_data.days,
                  prolong_price: prolong_data.price,
                  prolong_days: prolong_data.days,
                  prolong_end_date: moment(booking.end_date, "DD-MM-YYYY H:mm")
                    .add(prolong_data.days, "days")
                    .format("DD.MM.YYYY"),
                  date: moment().format("DD/MM/YY"),
                  date_short: moment().format("DD/MM"),
                  serial_number: booking.serial_number ? booking.serial_number : "      ",
                  employee_full_name: self.user_full_name,
                  employee_short_name: self.user_fio,
                  company_signer: self.user_full_name,
                  client_signer: self.docxSignerName(client),
                  first_name: client.name ? client.name : "      ",
                  middle_name: client.middlename ? client.middlename : "      ",
                  last_name: client.lastname ? client.lastname : "      ",
                  short_name: client.fio ? client.fio : "      ",
                  client_id: client.id ? client.id : "      ",
                  booking_id: booking.id ? booking.id : "      ",
                  car_id: car.id ? car.id : "      ",
                  date_birth: client.birthday
                    ? moment(client.birthday, "YYYY-MM-DD").format("DD-MM-YYYY")
                    : "      ",
                  passport_series: client.passport_series
                    ? client.passport_series
                    : "      ",
                  passport_number: client.passport_number
                    ? client.passport_number
                    : "      ",
                  passport_issued: client.passport_issued
                    ? client.passport_issued
                    : "      ",
                  passport_issued_date: client.passport_issued_date
                    ? moment(client.passport_issued_date, "YYYY-MM-DD").format("L")
                    : "      ",
                  passport_expired_date: client.passport_expired_date
                    ? moment(client.passport_expired_date, "YYYY-MM-DD").format("L")
                    : "      ",
                  driver_series: client.driver_series
                    ? client.driver_series
                    : "      ",
                  driver_number: client.driver_number
                    ? client.driver_number
                    : "      ",
                  driver_issued: client.driver_issued
                    ? client.driver_issued
                    : "      ",
                  driver_issued_date: client.driver_issued_date
                    ? moment(client.driver_issued_date, "YYYY-MM-DD").format("L")
                    : "      ",
                  driver_expired_date: client.driver_expired_date
                    ? moment(client.driver_expired_date, "YYYY-MM-DD").format("L")
                    : "      ",
                  taxi_license: client.taxi_license
                    ? client.taxi_license
                    : "      ",
                  inn: client.inn ? client.inn : "      ",
                  country: client.country ? client.country : "      ",
                  city: client.city ? client.city : "      ",
                  address: client.address ? client.address : "      ",
                  phone: client.phone ? client.phone : "      ",
                  entity_adress: client.entity_adress
                    ? client.entity_adress
                    : "     ",
                  entity_address: client.entity_adress
                    ? client.entity_adress
                    : "     ",
                  entity_name: client.entity_name
                    ? client.entity_name
                    : "     ",
                  ceo: client.ceo ? client.ceo : "     ",
                  reg_form: client.reg_form ? client.reg_form : "     ",
                  entity_phone: client.entity_phone
                    ? client.entity_phone
                    : "     ",
                  ogrn: client.ogrn ? client.ogrn : "     ",
                  acc_number: client.acc_number ? client.acc_number : "     ",
                  bank: client.bank ? client.bank : "     ",
                  korr: client.korr ? client.korr : "     ",
                  bik: client.bik ? client.bik : "     ",
                  email: client.email ? client.email : "     ",
                  doc_number: client.doc_number ? client.doc_number : "     ",
                  start_date: booking.start_date
                    ? booking.start_date
                    : "      ",
                  end_date: booking.end_date ? booking.end_date : "      ",
                  start_date_short: booking.start_date
                    ? moment(booking.start_date, "DD-MM-YYYY H:mm").format(
                      "DD.MM.YYYY"
                    )
                    : "     ",
                  end_date_short: booking.end_date
                    ? moment(booking.end_date, "DD-MM-YYYY H:mm").format(
                      "DD.MM.YYYY"
                    )
                    : "     ",
                  day_cost: car.prices.find((price) => price.season_id === null)
                    ? car.prices.find((price) => price.season_id === null)
                      .values[0]
                    : booking.start_price,
                  price: booking.price ? booking.price : "      ",
                  day_cost_without_commission: car.prices && car.prices.find((price) => price.season_id === null)
                    ? (car.prices.find((price) => price.season_id === null)
                      .values[0] - commission_day).toFixed(2)
                    : booking.start_price,
                  price_without_commission: booking.price
                    ? (booking.price - commission_day).toFixed(2)
                    : "      ",
                  cost_without_commission: booking.rental_cost
                    ? (booking.rental_cost - commission).toFixed(2)
                    : "      ",
                  selected_price: booking.selected_price
                    ? booking.selected_price
                    : "      ",
                  cost: booking.rental_cost ? booking.rental_cost : "      ",
                  days: booking.days ? booking.days : "      ",
                  location_start: booking.location_start
                    ? booking.location_start
                    : "      ",
                  location_end: booking.location_end
                    ? booking.location_end
                    : "      ",
                  delivery_start: booking.delivery
                    ? booking.delivery
                    : "      ",
                  delivery_end: booking.delivery_end
                    ? booking.delivery_end
                    : "      ",
                  equipment: booking.equipment ? booking.equipment : "      ",
                  hours_cost: booking.hours_cost
                    ? booking.hours_cost
                    : "      ",
                  additional_hours: booking.additional_hours
                    ? booking.additional_hours
                    : "      ",
                  add_drivers_cost: booking.add_drivers_cost
                    ? booking.add_drivers_cost
                    : "      ",
                  insurance: booking.insurance ? booking.insurance : "      ",
                  clean: booking.clean_payment
                    ? booking.clean_payment
                    : "      ",
                  other: booking.other ? booking.other : "      ",
                  mileage_limit: car.extra_mileage_km ? car.extra_mileage_km : this.company.mileage_limit || 0,
                  extra_mileage_km: car.extra_mileage_price ? car.extra_mileage_price : this.company.mileage_limit_cost || 0,
                  has_delivery_start: booking.delivery ? true : false,
                  has_delivery_end: booking.delivery_end ? true : false,
                  has_equipment: booking.equipment ? true : false,
                  has_additional_hours: booking.additional_hours ? true : false,
                  has_hours_cost: booking.hours_cost ? true : false,
                  has_add_drivers_cost: booking.add_drivers_cost ? true : false,
                  has_insurance: booking.insurance ? true : false,
                  has_clean: booking.clean_payment ? true : false,
                  has_other: booking.other ? true : false,
                  has_sale: booking.sale ? true : false,
                  has_sale_cash: booking.sale_cash ? true : false,
                  sale: booking.sale ? booking.sale : "",
                  sale_cash: booking.sale_cash ? booking.sale_cash : "",
                  start_mileage: booking.start_mileage
                    ? booking.start_mileage
                    : "      ",
                  end_mileage: booking.end_mileage
                    ? booking.end_mileage
                    : "      ",
                  car_name: booking.car_name ? booking.car_name : "      ",
                  car_code: booking.car_code ? booking.car_code : "      ",
                  car_number: car.number ? car.number : "      ",
                  car_vin: car.vin ? car.vin : "      ",
                  car_body: car.body_number ? car.body_number : "      ",
                  car_pts: car.pts ? car.pts : "      ",
                  car_purchase_price: car.purchase_price ? car.purchase_price : "      ",
                  car_reg: car.registration_certificate
                    ? car.registration_certificate
                    : "      ",
                  car_color: car.color ? car.color : "      ",
                  car_year: car.year ? car.year : "      ",
                  car_fuel: car.fuel ? car.fuel : "      ",
                  price_hour: car.price_hour ? car.price_hour : "      ",
                  additional_services:
                    additional_services > 0 ? additional_services : "      ",
                  has_additional_services:
                    additional_services > 0 ? true : false,
                  deposit: this.$emit('current-deposit') ? this.$emit('current-deposit') : "      ",
                  total: booking.total ? booking.total : "      ",
                  total_without_commission:
                    booking.total
                      ? booking.total - commission
                      : "      ",
                  total_with_deposit:
                    booking.total && this.$emit('current-deposit')
                      ? booking.total + this.$emit('current-deposit')
                      : "      ",
                  total_with_deposit_without_commission:
                    booking.total && this.$emit('current-deposit')
                      ? booking.total + this.$emit('current-deposit') - commission
                      : "      ",
                  prepaid: prepaid,
                  add_drivers_info: add_drivers_info,
                  repair_cost: car.repair_cost ? car.repair_cost : "      ",
                  franchise: car.franchise ? car.franchise : "      ",
                  max_fine: car.max_fine ? car.max_fine : "      ",
                  custom_field_1: booking.custom_field_1
                    ? booking.custom_field_1
                    : "      ",
                  custom_field_2: booking.custom_field_2
                    ? booking.custom_field_2
                    : "      ",
                  custom_field_3: booking.custom_field_3
                    ? booking.custom_field_3
                    : "      ",
                  cars_custom_field_1: car.custom_field_1
                    ? car.custom_field_1
                    : "      ",
                  cars_custom_field_2: car.custom_field_2
                    ? car.custom_field_2
                    : "      ",
                  cars_custom_field_3: car.custom_field_3
                    ? car.custom_field_3
                    : "      ",
                });

                doc.render();

                var out = doc.getZip().generate({
                  type: "blob",
                  mimeType:
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                }); //Output the document using Data-URI
                let date = moment().format("Y_MM_DD");
                // console.log(client)
                if (!self.isNative) {
                  saveAs(
                    out,
                    `${self.$t("bookings.prolong_short_low")}_` +
                    date +
                    "_" +
                    booking.id +
                    ".docx"
                  );
                } else {
                  if (Filesystem.checkPermissions()) {
                    const getBase64FromBlob = async (blob) => {
                      return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = () => {
                          const base64data = reader.result;
                          resolve(base64data);
                        }
                      });
                    }
                    getBase64FromBlob(out).then(
                      (base64) => {
                        // save file
                        async function writeFile() {
                          return await Filesystem.writeFile({
                            path: `${self.$t("bookings.prolong_short_low")}_` + date + "_" + booking.id + ".docx",
                            data: base64,
                            directory: Directory.Documents,
                          });
                        }
                        let file_uri = null
                        let writedFile = writeFile().then((getUriResult) => {
                          // share file opened file
                          let shareFile = async () => {
                            await Share.share({
                              title: 'Download document',
                              files: [getUriResult.uri],
                            }).catch((error) => {
                              console.log("shareFile", error)
                            });
                          };
                          let sharedFile = shareFile();
                        }).catch((error) => {
                          console.log("shareFile", error)
                        });
                      }
                    );
                  } else {
                    Filesystem.requestPermissions();
                  }
                }
              })
              .catch(function (error) {
                console.log(error);
                self.$notify(self.$t("errors.gen_template_title"), self.$t("errors.gen_template_text"), "error");
              });
          })
          .catch((error) => {
            this.setError(error, this.$t("errors.upload_template"));
            console.log(error);
          })
          .finally(() => (this.isLoadingGenerateProlongDoc = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    translateCountGroup(group) {
      this.$emit('translate-count-group', group);
      // Предполагаем, что родительский компонент обрабатывает этот метод
      // или можно перенести логику сюда, если она доступна
    },
    formatCreatedAt(date) {
      this.$emit('format-created-at', date);
      // Предполагаем, что родительский компонент обрабатывает этот метод
      // или можно перенести логику сюда, если она доступна
    },
    async copyToBuffer(text) {
      try {
        await copyTextToClipboard(text);
        this.$notify(this.$t("custom.copied_to_buffer"), "", "success");
      } catch (error) {
        this.$notify(this.$t("custom.copy_error"), "", "error");
      }
    },
  }
}
</script>

<style scoped>
.payment {
  flex-wrap: wrap;
}

/* Баннер набран text-overline (капитель, разрядка) — для таблицы расчёта это
   нечитаемо, поэтому внутри разбивки возвращаем обычный текст. */
.prolong-breakdown {
  text-transform: none;
  letter-spacing: normal;
  font-size: 13px;
  line-height: 1.5;
}

/* Панель живёт внутри баннера: свой фон и отступы карточки ей не нужны,
   а текст заголовка обязан наследовать цвет баннера (info/error). */
.prolong-preview-panels ::v-deep .v-expansion-panel,
.prolong-preview-panels ::v-deep .v-expansion-panel-header,
.prolong-preview-panels ::v-deep .v-expansion-panel-content__wrap {
  background: transparent;
  color: inherit;
}

.prolong-preview-panels ::v-deep .v-expansion-panel-header {
  position: relative;
  min-height: 32px;
  /* Поля симметричны ширине шеврона: без этого строка вставала в центр
     ОСТАВШЕЙСЯ ширины, то есть визуально левее середины баннера. */
  padding: 0 40px;
}

.prolong-preview-panels ::v-deep .v-expansion-panel-header__icon {
  position: absolute;
  right: 8px;
}

.prolong-preview-panels ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 0 8px;
}

.prolong-preview-panels ::v-deep .v-expansion-panel-header__icon .v-icon {
  color: inherit !important;
}

/* Заголовок заперт, когда объяснять нечего, — но остаётся читаемым. */
.prolong-preview-panels ::v-deep .v-expansion-panel--disabled .v-expansion-panel-header {
  opacity: 1;
}

/* Строки расчёта — часть баннера, а не отдельная таблица данных: снимаем
   собственный фон и разлиновку v-data-table, колонки прижимаем влево (на всю
   ширину диалога дата и цена расходились по разным краям экрана). */
.prolong-breakdown ::v-deep .v-data-table {
  background: transparent;
  color: inherit !important;
}

.prolong-breakdown ::v-deep table {
  width: auto;
}

.prolong-breakdown ::v-deep td,
.prolong-breakdown ::v-deep th {
  height: 24px !important;
  padding: 0 16px 0 0 !important;
  font-size: 13px !important;
  color: inherit !important;
  border-bottom: none !important;
}
</style>
